import type { Selection } from "react-aria-components";
import { TableBody } from "react-aria-components";
import { useCallback, useMemo } from "react";

import type { CosignatureQueueResponse } from "@/core/types";
import { EmptyTable } from "@/ui2/molecules/(table)/EmptyTable";
import { Table2 } from "@/ui2/atoms/(table)/Table";
import { useTableSort } from "@/lib/hooks/useTableSort";

import { useCosignatureModal } from "../CosignatureModalProvider";

import type { AdaptedQueueRow } from "./helpers/adapters";
import { adaptQueueData } from "./helpers/adapters";
import { CosignatureTableHeader } from "./components/CosignatureTableHeader";
import { CosignatureTableRow } from "./components/CosignatureTableRow";
import { CosignatureTableSkeleton } from "./components/CosignatureTableSkeleton";
import { isStatusSettled } from "./helpers/isStatusSettled";

type CosignatureTableProps = {
  data: CosignatureQueueResponse | undefined;
  isLoading: boolean;
  onClearFilters?: VoidFunction;
  onSelectedRowsChange: (rows: AdaptedQueueRow[]) => void;
  selectedIds: string[];
};

// stable reference — prevents useMemo in useTableSort from recomputing every render
const KEY_MAP = { dateSigned: "signatureDate" } satisfies Partial<
  Record<string, keyof AdaptedQueueRow>
>;

export function CosignatureTable({
  data,
  isLoading,
  onClearFilters,
  onSelectedRowsChange,
  selectedIds,
}: CosignatureTableProps) {
  const { openModal } = useCosignatureModal();

  const rows = useMemo(() => adaptQueueData(data ?? []), [data]);

  const { onSortChange, sortDescriptor, sortedRows } = useTableSort<AdaptedQueueRow>(rows, {
    initialSortDescriptor: { column: "dateSigned", direction: "descending" },
    keyMap: KEY_MAP,
  });

  const disabledKeys = useMemo(
    () => new Set(rows.filter((r) => isStatusSettled(r.status)).map((r) => r.id)),
    [rows],
  );

  const handleSelectionChange = useCallback(
    (selection: Selection) => {
      if (selection === "all") {
        onSelectedRowsChange(sortedRows.filter((r) => !isStatusSettled(r.status)));
      } else {
        const selectedSet = selection as Set<string>;

        onSelectedRowsChange(rows.filter((r) => selectedSet.has(r.id)));
      }
    },
    [rows, sortedRows, onSelectedRowsChange],
  );

  // stable Set reference prevents react-aria's TableCollection from rebuilding on every render
  const selectedKeys = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <Table2
      aria-label="Co-signature notes"
      disabledKeys={disabledKeys}
      onSelectionChange={handleSelectionChange}
      onSortChange={onSortChange}
      selectedKeys={selectedKeys}
      selectionBehavior="toggle"
      selectionMode="multiple"
      sortDescriptor={sortDescriptor}
    >
      <CosignatureTableHeader isDisabled={isLoading} />

      {isLoading ? (
        <CosignatureTableSkeleton />
      ) : (
        <TableBody
          items={sortedRows}
          renderEmptyState={() => (
            <EmptyTable
              ctaProps={onClearFilters && { label: "Clear Filters", onPress: onClearFilters }}
              message="No notes to review"
            />
          )}
        >
          {(row) => (
            <CosignatureTableRow onAction={(action) => openModal({ action, row })} row={row} />
          )}
        </TableBody>
      )}
    </Table2>
  );
}
